import React, { useRef } from "react";
import { Autocomplete, Box, Button, TextField } from "@mui/material";
import Panel from "../../../../components/Panel";
import EditScreen, { EditScreenHandle } from "../EditScreen";
import StatefulButton from "../../../../components/StatefulButton/StatefulButton";
import * as NotificationActions from "../../../../actions/notification";
import {
  getLabTestGroupById,
  getLabTestGroups,
  getLabTests,
  removeDraftGroup,
  removeSelectedTests,
  setTestGroupDraft
} from "../../../../slices/labTestSettingsSlice";
import { RootState } from "../../../../store";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import LabGroupResetToDefault from "./LabGroupResetToDefault";
import { Test, TestTypes } from "../../../../interfaces/Lab";
import hasOwnProperty from "../../../../helpers/object";

interface Props {
  onClose: () => void;
  groupName?: string;
}

const LabTestGroupEditPanel = ({ onClose, groupName }: Props): JSX.Element => {
  const [isAboutToReset, setIsAboutToReset] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [selectedGroup, setSelectedGroup] = React.useState(null);

  const editScreenRef = useRef<EditScreenHandle>(null);
  const dispatch = useAppDispatch();

  const selectedTestsUUIDs = useAppSelector((state: RootState) => state.labSettings.checkedUUIDs);
  const testGroupsMaster = useAppSelector((state: RootState) => state.labSettings.labTestGroups);
  const draftGroup = useAppSelector((state: RootState) => state.labSettings.draftGroup);

  React.useEffect(() => {
    if (!groupName) {
      dispatch(getLabTestGroups());
      dispatch(getLabTests());
      return () => dispatch(removeDraftGroup());
    }
    return undefined;
  }, [dispatch, groupName]);

  const isEditOnly = Boolean(groupName);
  const title = isEditOnly ? `Edit ${groupName}` : "Lab Test Group Create/Edit";

  return (
    <Panel
      onClose={onClose}
      title={title}
      wrapperStyle={{ minWidth: isEditOnly ? "70%" : 900 }}
      genericButton={
        <Box display="flex" gap={1}>
          <Button variant="outlined" color="primary" onClick={() => setIsAboutToReset(true)}>
            Reset To Defaults
          </Button>
          {selectedTestsUUIDs.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                dispatch(removeSelectedTests());
                dispatch(
                  NotificationActions.notificationAdd({
                    id: new Date().getUTCMilliseconds(),
                    variant: "warning",
                    message: "Selected Tests have been removed!",
                    autoTimeout: true
                  })
                );
              }}
            >
              Remove Selected Tests
            </Button>
          )}
        </Box>
      }
      footer={
        <Box width="100%" display="flex" justifyContent="flex-end" py={1} gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <StatefulButton
            color="primary"
            variant="contained"
            disabled={loading || !draftGroup?.labTests?.length}
            onClick={async () => {
              setLoading(true);
              await editScreenRef.current?.handleSave();
              setLoading(false);
            }}
            isLoading={loading}
            circularProgressProps={{ size: 16 }}
            data-testmation="saveTestGroupEdit"
          >
            Save
          </StatefulButton>
        </Box>
      }
    >
      <Box>
        {!isEditOnly && (
          <Autocomplete<Test | { inputValue: string; name: string }>
            sx={{ mt: "16px" }}
            getOptionLabel={(o) => o.name}
            value={selectedGroup}
            options={testGroupsMaster}
            renderOption={(props, option) => (
              <li {...props} key={(hasOwnProperty(option, "id") && option?.id) || option.name}>
                <div>{option.name}</div>
              </li>
            )}
            filterOptions={(options, params) => {
              if (params.inputValue !== "") {
                options.push({ inputValue: params.inputValue, name: `Add "${params.inputValue}"` });
              }
              return options.filter((o) =>
                o.name.toLowerCase().includes(params.inputValue.toLowerCase())
              );
            }}
            onChange={(_, v) => {
              dispatch(removeDraftGroup());
              if (v) {
                if (hasOwnProperty(v, "inputValue") && v.inputValue) {
                  dispatch(
                    setTestGroupDraft({
                      id: null,
                      code: "",
                      name: v.inputValue,
                      type: "pathology" as TestTypes,
                      common: false,
                      category: "Radiology",
                      labTests: [],
                      additionalTestData: null,
                      interpretationTemplate: null,
                      info: { defaultRemarks: "" }
                    })
                  );
                  setSelectedGroup({
                    name: v.inputValue,
                    code: "",
                    common: false,
                    info: null,
                    type: "pathology",
                    category: "Radiology",
                    resourceCentreId: null,
                    referenceId: null
                  });
                } else {
                  setSelectedGroup(v);
                  dispatch(getLabTestGroupById(v.id));
                  if (hasOwnProperty(v, "documentRecord")) {
                    dispatch(setTestGroupDraft(v.documentRecord));
                  }
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                data-testmation="labSelect"
                {...params}
                margin="dense"
                fullWidth
                variant="outlined"
                label="Select Or Add Lab"
                placeholder="Select Or Add Lab"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
          />
        )}
      </Box>

      <EditScreen ref={editScreenRef} onCancel={onClose} />
      <LabGroupResetToDefault isOpen={isAboutToReset} onClose={() => setIsAboutToReset(false)} />
    </Panel>
  );
};

export default LabTestGroupEditPanel;
